// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// Cairn — the board layout, as a PURE function of (canvas size, pyramid height) so it can be
// pinned by layout.test.ts without a canvas (principle #20's ratchet): every mode's cairn, hand
// and scree must fit at a phone width AND a desktop width with no horizontal overflow and usable
// (≥28px-ish) stones. The pyramid stacks tip-up: row r has r+1 stones, each row nudged down by a
// fraction of a stone so a lower row is tucked under the one above (classic cairn overlap). The
// hand stone and the scree pile sit in a strip below the cairn.

import { pyramidLayout } from './board';

export interface StoneRect {
  id: number;
  row: number;
  col: number;
  cx: number;
  cy: number;
  w: number;
  h: number;
}
export interface Slot {
  cx: number;
  cy: number;
  w: number;
  h: number;
}
export interface CairnLayout {
  cw: number;
  ch: number;
  stoneW: number;
  stoneH: number;
  stones: StoneRect[];
  hand: Slot;
  stock: Slot;
}

const ASPECT = 1.34; // stone height / width
const V_OVERLAP = 0.62; // each row's centre steps down this fraction of a stone height
const MIN_STONE_W = 26;
const MAX_STONE_W = 78;

/** Pure layout for a pyramid of height `h` in a `cw × ch` box. */
export function computeLayout(cw: number, ch: number, h: number): CairnLayout {
  const { rc } = pyramidLayout(h);

  // width bound: the base row of `h` stones spans h*stoneW across 94% of the width.
  const wByWidth = (cw * 0.94) / h;
  // height bound: pyramid height + a strip for the hand/scree must fit 96% of the height.
  //   pyramidH = stoneH * ((h-1)*V_OVERLAP + 1); strip ≈ stoneH * 1.85 (hand + scree + gaps)
  const hByHeight = (ch * 0.96) / ((h - 1) * V_OVERLAP + 1 + 1.85);
  const stoneH = Math.min(wByWidth * ASPECT, hByHeight);
  let stoneW = stoneH / ASPECT;
  stoneW = Math.max(MIN_STONE_W, Math.min(MAX_STONE_W, stoneW));
  const sh = stoneW * ASPECT;

  const dx = stoneW; // base stones sit side by side
  const dy = sh * V_OVERLAP;
  const topPad = Math.max(8, ch * 0.03);

  const stones: StoneRect[] = rc.map((p, id) => ({
    id,
    row: p.row,
    col: p.col,
    cx: cw / 2 + (p.col - p.row / 2) * dx,
    cy: topPad + p.row * dy + sh / 2,
    w: stoneW,
    h: sh,
  }));

  const pyramidBottom = topPad + (h - 1) * dy + sh;
  const gap = sh * 0.32;
  const stripCy = pyramidBottom + gap + (sh * 1.18) / 2;
  const hand: Slot = { cx: cw * 0.37, cy: stripCy, w: stoneW * 1.2, h: sh * 1.18 };
  const stock: Slot = { cx: cw * 0.66, cy: stripCy, w: stoneW * 1.06, h: sh * 1.18 };

  return { cw, ch, stoneW, stoneH: sh, stones, hand, stock };
}
